import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SENHA_MINIMA = 6; // a mesma regra do backend (routes/senha.py) e do Perfil

// Página do link de nova senha (Adnéia, 20/10/2026). Abre sem login. O código vem
// depois do "#" (o navegador não manda essa parte ao servidor). Um script no topo do
// public/index.html já guardou o código e limpou o endereço antes dos scripts de fora
// (Emergent, PostHog); sem esse guardado, a página lê do próprio endereço.
const GUARDADO = 'elo_link_senha';
const lerCodigo = () => {
  let fonte = window.location.hash || '';
  try {
    fonte = sessionStorage.getItem(GUARDADO) || fonte;
  } catch (e) { /* sem sessionStorage: fica o endereço */ }
  const m = fonte.match(/[#&]t=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : '';
};

const RedefinirSenha = () => {
  const navigate = useNavigate();
  const [codigo] = useState(lerCodigo);
  const [fase, setFase] = useState('carregando'); // carregando | form | invalido | pronto
  const [nome, setNome] = useState('');
  const [aviso, setAviso] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    try { sessionStorage.removeItem(GUARDADO); } catch (e) { /* nada guardado */ }
    if (!codigo) {
      setAviso('Este link está incompleto. Peça um link novo à gestão do ELO.');
      setFase('invalido');
      return;
    }
    axios.post(`${API_URL}/api/auth/redefinir-senha/validar`, { t: codigo })
      .then((r) => {
        setNome(r.data?.nome || '');
        setFase('form');
      })
      .catch((e) => {
        setAviso(e.response?.data?.detail || 'Não consegui conferir o link. Tente de novo em alguns minutos.');
        setFase('invalido');
      });
  }, [codigo]);

  const salvar = async (e) => {
    e.preventDefault();
    if (nova.length < SENHA_MINIMA) {
      toast.error(`A senha nova precisa ter pelo menos ${SENHA_MINIMA} caracteres.`);
      return;
    }
    if (nova !== confirma) {
      toast.error('As duas senhas não são iguais.');
      return;
    }
    setSalvando(true);
    try {
      await axios.post(`${API_URL}/api/auth/redefinir-senha/confirmar`, { t: codigo, nova_senha: nova });
      setNova('');
      setConfirma('');
      setFase('pronto');
    } catch (err) {
      const detalhe = err.response?.data?.detail || 'Não consegui gravar a senha nova.';
      if (typeof detalhe === 'string' && detalhe.includes('link')) {
        setAviso(detalhe);
        setFase('invalido');
      } else {
        toast.error(detalhe);
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="redefinir-senha-page">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-lg">E</span>
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">
            Senha nova do ELO
          </CardTitle>
          <CardDescription>
            {fase === 'form' && (nome ? `Olá, ${nome}. Crie a sua senha nova.` : 'Crie a sua senha nova.')}
            {fase === 'carregando' && 'Conferindo o link...'}
            {fase === 'pronto' && 'Pronto.'}
            {fase === 'invalido' && 'Link sem validade.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fase === 'carregando' && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {fase === 'invalido' && (
            <div className="space-y-4">
              <p className="text-sm flex items-start gap-2 text-amber-900 dark:text-amber-100">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                {aviso}
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                Ir para o login
              </Button>
            </div>
          )}

          {fase === 'pronto' && (
            <div className="space-y-4">
              <p className="text-sm flex items-start gap-2 text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                Senha nova gravada. Entre no ELO com ela. Se o navegador guardou a senha antiga, troque lá também.
              </p>
              <Button className="w-full" onClick={() => navigate('/login')} data-testid="btn-ir-login">
                Ir para o login
              </Button>
            </div>
          )}

          {fase === 'form' && (
            <form onSubmit={salvar} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nova-senha">Senha nova</Label>
                <div className="relative">
                  <Input
                    id="nova-senha"
                    type={mostrar ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={nova}
                    onChange={(e) => setNova(e.target.value)}
                    disabled={salvando}
                    className="pr-10"
                    data-testid="input-nova-senha"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrar(!mostrar)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {mostrar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Pelo menos {SENHA_MINIMA} caracteres.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirma-senha">Repita a senha nova</Label>
                <Input
                  id="confirma-senha"
                  type={mostrar ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirma}
                  onChange={(e) => setConfirma(e.target.value)}
                  disabled={salvando}
                  data-testid="input-confirma-senha"
                />
              </div>
              <Button type="submit" className="w-full" disabled={salvando} data-testid="btn-gravar-senha">
                {salvando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gravando...
                  </>
                ) : (
                  'Gravar senha nova'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RedefinirSenha;
